// frontend/src/hooks/useSessionPolicy.js
// S1 — délai d'inactivité fourni par le SERVEUR (contexte /me) au lieu des 20-30 min codées en dur.
// Le backend (adminContextController) renvoie session = { inactivity_minutes, warning_minutes } selon
// le rôle (source unique sessionGuard). On lit ces valeurs ; si elles manquent (endpoint indisponible
// pour le portail public/driver, erreur réseau, ancien backend), on REPLIE sur la valeur passée en
// défaut (fallbackMinutes) — jamais de blocage.
//
// L'endpoint /me/context est ADMIN-only (/api/admin/me/context) : on ne le sollicite donc que pour les
// rôles admin/staff. Pour CLIENT/DRIVER on garde le repli (pas de régression).
import { useState, useEffect } from 'react';
import { adminService } from '../api';

const ADMIN_LIKE = new Set(['SUPER_ADMIN', 'ADMIN_COMPANY', 'admin']);

/**
 * @param {object} opts
 * @param {boolean} opts.enabled      n'interroge le serveur que si vrai (authentifié + rôle admin-like).
 * @param {string}  opts.role         rôle courant (décide si /me/context est joignable).
 * @param {number}  opts.fallbackMinutes  repli si le serveur ne fournit pas la valeur.
 * @param {number}  [opts.fallbackWarningMinutes]  repli pour l'avertissement.
 * @returns {{ inactivityMs:number, warningMs:(number|undefined), fromServer:boolean }}
 */
export default function useSessionPolicy({ enabled, role, fallbackMinutes, fallbackWarningMinutes } = {}) {
  const [policy, setPolicy] = useState({ inactivityMinutes: null, warningMinutes: null });

  useEffect(() => {
    let alive = true;
    // On n'appelle le serveur que là où l'endpoint existe (admin-like). Sinon : repli direct.
    if (!enabled || !ADMIN_LIKE.has(role)) { setPolicy({ inactivityMinutes: null, warningMinutes: null }); return; }
    (async () => {
      try {
        const res = await adminService.getMyContext();
        const s = res?.data?.data?.session || null;
        if (!alive) return;
        if (s && Number(s.inactivity_minutes) > 0) {
          setPolicy({ inactivityMinutes: Number(s.inactivity_minutes), warningMinutes: Number(s.warning_minutes) || null });
        } else {
          setPolicy({ inactivityMinutes: null, warningMinutes: null }); // repli
        }
      } catch {
        if (alive) setPolicy({ inactivityMinutes: null, warningMinutes: null }); // repli silencieux
      }
    })();
    return () => { alive = false; };
  }, [enabled, role]);

  const minutes = policy.inactivityMinutes || fallbackMinutes || 30;
  const warnMin = policy.warningMinutes || fallbackWarningMinutes || null;
  return {
    inactivityMs: minutes * 60 * 1000,
    warningMs: warnMin ? warnMin * 60 * 1000 : undefined,
    fromServer: policy.inactivityMinutes != null,
  };
}
